import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, StyleSheet, TouchableOpacity, TextInput, Image, Alert, ScrollView, Platform, ActivityIndicator, Modal, TouchableWithoutFeedback, FlatList } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Text } from '@/components';
import { useRouter, Stack, useLocalSearchParams } from 'expo-router';
import { useDogs } from '@/context/DogsContext';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/lib/api';
import * as ImagePicker from 'expo-image-picker';
import * as Haptics from 'expo-haptics';
import { ChevronDown, ChevronRight, Camera, ArrowLeft, Search, X } from 'lucide-react-native';
import theme from '@/constants/colors';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styles from './dog-profile.styles';
import { openAppSettings } from '@/utils/permissions';
import { logEvent } from '@/lib/analytics';
import { ensureJpegImage } from '@/utils/imageConversion';
import { lookupBreed, getBreedNames, BREED_SIZE_TO_API, DogSize } from '@/constants/dogBreeds';
import { useTranslation } from 'react-i18next';
import Slider from '@react-native-community/slider';
import DogOwnersSection from '@/components/DogOwnersSection';

// ── Trait definitions ──────────────────────────────────────────────────────────

const SLIDER_TRAITS = [
  { key: 'dogTolerance', labelKey: 'dogProfile.sliderDogTolerance', leftKey: 'dogProfile.sliderDogToleranceLeft', rightKey: 'dogProfile.sliderDogToleranceRight', descKey: 'dogProfile.sliderDogToleranceDesc' },
  { key: 'offleashReliability', labelKey: 'dogProfile.sliderOffLeashReliability', leftKey: 'dogProfile.sliderOffLeashReliabilityLeft', rightKey: 'dogProfile.sliderOffLeashReliabilityRight', descKey: 'dogProfile.sliderOffLeashReliabilityDesc' },
  { key: 'stimulationTolerance', labelKey: 'dogProfile.sliderStimulationTolerance', leftKey: 'dogProfile.sliderStimulationToleranceLeft', rightKey: 'dogProfile.sliderStimulationToleranceRight', descKey: 'dogProfile.sliderStimulationToleranceDesc' },
  { key: 'walkingNeed', labelKey: 'dogProfile.sliderWalkingNeed', leftKey: 'dogProfile.sliderWalkingNeedLeft', rightKey: 'dogProfile.sliderWalkingNeedRight', descKey: 'dogProfile.sliderWalkingNeedDesc' },
] as const;

const TOGGLE_TRAITS = [
  { key: 'nervousAroundPeople', labelKey: 'dogProfile.sliderNervousAroundPeople', descKey: 'dogProfile.sliderNervousAroundPeopleDesc' },
  { key: 'highPreyDrive', labelKey: 'dogProfile.sliderHighPreyDrive', descKey: 'dogProfile.sliderHighPreyDriveDesc' },
  { key: 'sensitiveToHeat', labelKey: 'dogProfile.sliderSensitiveToHeat', descKey: 'dogProfile.sliderSensitiveToHeatDesc' },
] as const;

// ── Component ─────────────────────────────────────────────────────────────────

export default function DogProfileScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { dogProfile, dogProfiles, updateDogProfile, refreshDogProfile, createDogProfile } = useDogs();
  const { session } = useAuth();
  const { user } = useAuth();
  const { t } = useTranslation();
  const params = useLocalSearchParams<{ from?: string; newDog?: string; dogId?: string }>();
  const openedFromTab = params.from === 'profile' || params.from === 'settings';
  const openedFromSettings = params.from === 'settings';
  const isNewDog = params.newDog === 'true';
  const targetDogId = params.dogId;
  const targetDog = useMemo(() => {
    if (isNewDog) return null;
    if (targetDogId) return dogProfiles.find(d => d.id === targetDogId) || null;
    return dogProfile;
  }, [isNewDog, targetDogId, dogProfiles, dogProfile]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [nickname, setNickname] = useState<string>('');
  const [age, setAge] = useState<string>('');
  const [dobDate, setDobDate] = useState<Date | null>(null);
  const [tempDobDate, setTempDobDate] = useState<Date | null>(null);
  const [showDOBPicker, setShowDOBPicker] = useState<boolean>(false);
  const [photo, setPhoto] = useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState<boolean>(false);

  // Breed picker state
  const [breed, setBreed] = useState<string>('');
  const [showBreedPicker, setShowBreedPicker] = useState<boolean>(false);
  const [breedSearch, setBreedSearch] = useState<string>('');

  // ── Trait state ──────────────────────────────────────────────────────────────
  const [dogTolerance, setDogTolerance] = useState<number>(5);
  const [nervousAroundPeople, setNervousAroundPeople] = useState<boolean>(false);
  const [offleashReliability, setOffleashReliability] = useState<number>(5);
  const [stimulationTolerance, setStimulationTolerance] = useState<number>(5);
  const [highPreyDrive, setHighPreyDrive] = useState<boolean>(false);
  const [walkingNeed, setWalkingNeed] = useState<number>(5);
  const [sensitiveToHeat, setSensitiveToHeat] = useState<boolean>(false);

  // ── Trait modal state ────────────────────────────────────────────────────────
  const [activeTrait, setActiveTrait] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<number>(5);
  const [tempToggle, setTempToggle] = useState<boolean>(false);

  // ── Size state ──────────────────────────────────────────────────────────────
  const SIZE_OPTIONS: { key: DogSize; labelKey: string; api: string }[] = [
    { key: 'tiny', labelKey: 'dogProfile.sizeTiny', api: 'XS' },
    { key: 'small', labelKey: 'dogProfile.sizeSmall', api: 'S' },
    { key: 'medium', labelKey: 'dogProfile.sizeMedium', api: 'M' },
    { key: 'large', labelKey: 'dogProfile.sizeLarge', api: 'L' },
    { key: 'giant', labelKey: 'dogProfile.sizeGiant', api: 'XL' },
  ];
  const [dogSize, setDogSize] = useState<DogSize>('medium');
  const [showSizePicker, setShowSizePicker] = useState<boolean>(false);

  // All breed names (sorted)
  const allBreedNames = useMemo(() => getBreedNames(), []);

  // Filtered breed names based on search
  const filteredBreeds = useMemo(() => {
    if (!breedSearch.trim()) return allBreedNames;
    const q = breedSearch.toLowerCase().trim();
    return allBreedNames.filter(b => b.toLowerCase().includes(q));
  }, [allBreedNames, breedSearch]);

  // Track whether the breed was explicitly changed by the user
  // (as opposed to being loaded from existing profile data)
  const breedChangedByUser = useRef(false);

  // Derived size + traits from breed
  const breedData = useMemo(() => (breed ? lookupBreed(breed) : null), [breed]);

  // ── Sync traits + size from breed data ──────────────────────────────────────
  // Only apply breed defaults when the user explicitly changes the breed.
  // When loading an existing profile, traits come from the backend instead.
  useEffect(() => {
    if (breedData && breedChangedByUser.current) {
      breedChangedByUser.current = false;
      const t = breedData.traits;
      setDogSize(breedData.profile.size);
      setDogTolerance(Math.max(1, Math.round(t.dogTolerance / 10)));
      setNervousAroundPeople(t.nervousAroundPeople);
      setOffleashReliability(Math.max(1, Math.round(t.offleashReliability / 10)));
      setStimulationTolerance(Math.max(1, Math.round(t.stimulationTolerance / 10)));
      setHighPreyDrive(t.highPreyDrive);
      setWalkingNeed(Math.max(1, Math.round(t.walkingNeed / 10)));
      setSensitiveToHeat(t.sensitiveToHeat);
    }
  }, [breedData]);

  // ── Trait helpers ────────────────────────────────────────────────────────────
  const getSliderValue = (key: string): number => {
    switch (key) {
      case 'dogTolerance': return dogTolerance;
      case 'offleashReliability': return offleashReliability;
      case 'stimulationTolerance': return stimulationTolerance;
      case 'walkingNeed': return walkingNeed;
      default: return 5;
    }
  };

  const setSliderValue = (key: string, value: number) => {
    switch (key) {
      case 'dogTolerance': setDogTolerance(value); break;
      case 'offleashReliability': setOffleashReliability(value); break;
      case 'stimulationTolerance': setStimulationTolerance(value); break;
      case 'walkingNeed': setWalkingNeed(value); break;
    }
  };

  const getToggleValue = (key: string): boolean => {
    switch (key) {
      case 'nervousAroundPeople': return nervousAroundPeople;
      case 'highPreyDrive': return highPreyDrive;
      case 'sensitiveToHeat': return sensitiveToHeat;
      default: return false;
    }
  };

  const setToggleValue = (key: string, value: boolean) => {
    switch (key) {
      case 'nervousAroundPeople': setNervousAroundPeople(value); break;
      case 'highPreyDrive': setHighPreyDrive(value); break;
      case 'sensitiveToHeat': setSensitiveToHeat(value); break;
    }
  };

  const isSliderTrait = activeTrait
    ? SLIDER_TRAITS.some(t => t.key === activeTrait)
    : false;

  const activeSliderTrait = activeTrait
    ? SLIDER_TRAITS.find(t => t.key === activeTrait)
    : undefined;

  const activeToggleTrait = activeTrait
    ? TOGGLE_TRAITS.find(t => t.key === activeTrait)
    : undefined;

  const openSliderModal = (key: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setActiveTrait(key);
    setTempValue(getSliderValue(key));
  };

  const openToggleModal = (key: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setActiveTrait(key);
    setTempToggle(getToggleValue(key));
  };

  const confirmModal = () => {
    if (!activeTrait) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    if (isSliderTrait) {
      setSliderValue(activeTrait, Math.round(tempValue));
    } else {
      setToggleValue(activeTrait, tempToggle);
    }
    setActiveTrait(null);
  };

  const closeModal = () => {
    setActiveTrait(null);
  };

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isNewDog || !targetDog) return;
    setName(targetDog.name);
    setNickname(targetDog.nickname || '');
    if (targetDog.breed) setBreed(targetDog.breed);
    if (targetDog.dob) {
      const d = new Date(targetDog.dob);
      if (!isNaN(d.getTime())) {
        setAge(formatDateToDisplay(d));
        setDobDate(d);
      } else {
        setAge(targetDog.age?.toString() || '');
      }
    } else {
      setAge(targetDog.age?.toString() || '');
    }
    setPhoto(targetDog.image || null);

    // Load traits from backend (not breed defaults)
    if (targetDog.dogTolerance != null) setDogTolerance(Math.max(1, Math.round(targetDog.dogTolerance / 10)));
    if (targetDog.nervousAroundPeople != null) setNervousAroundPeople(targetDog.nervousAroundPeople);
    if (targetDog.offleashReliability != null) setOffleashReliability(Math.max(1, Math.round(targetDog.offleashReliability / 10)));
    if (targetDog.stimulationTolerance != null) setStimulationTolerance(Math.max(1, Math.round(targetDog.stimulationTolerance / 10)));
    if (targetDog.highPreyDrive != null) setHighPreyDrive(targetDog.highPreyDrive);
    if (targetDog.walkingNeed != null) setWalkingNeed(Math.max(1, Math.round(targetDog.walkingNeed / 10)));
    if (targetDog.sensitiveToHeat != null) setSensitiveToHeat(targetDog.sensitiveToHeat);

    // Load size from backend
    if (targetDog.size) {
      const sizeOption = SIZE_OPTIONS.find(o => o.api === targetDog.size);
      if (sizeOption) setDogSize(sizeOption.key);
    }
  }, [targetDog]);
  useEffect(() => {
    if (isNewDog || !openedFromSettings || !targetDog) return;
    // Populate form from the cached dog profile
    setName(targetDog.name || '');
    setNickname(targetDog.nickname || '');
    if (targetDog.breed) setBreed(targetDog.breed);
    if (targetDog.dob) {
      const d = new Date(targetDog.dob);
      if (!isNaN(d.getTime())) {
        setAge(formatDateToDisplay(d));
        setDobDate(d);
      } else {
        setAge(String(targetDog.age ?? ''));
      }
    } else {
      setAge(String(targetDog.age ?? ''));
    }
    setPhoto(targetDog.image || null);

    if (targetDog.size) {
      const sizeOption = SIZE_OPTIONS.find(o => o.api === targetDog.size);
      if (sizeOption) setDogSize(sizeOption.key);
    }

    if (targetDog.dogTolerance != null) setDogTolerance(Math.max(1, Math.round(targetDog.dogTolerance / 10)));
    if (targetDog.nervousAroundPeople != null) setNervousAroundPeople(targetDog.nervousAroundPeople);
    if (targetDog.offleashReliability != null) setOffleashReliability(Math.max(1, Math.round(targetDog.offleashReliability / 10)));
    if (targetDog.stimulationTolerance != null) setStimulationTolerance(Math.max(1, Math.round(targetDog.stimulationTolerance / 10)));
    if (targetDog.highPreyDrive != null) setHighPreyDrive(targetDog.highPreyDrive);
    if (targetDog.walkingNeed != null) setWalkingNeed(Math.max(1, Math.round(targetDog.walkingNeed / 10)));
    if (targetDog.sensitiveToHeat != null) setSensitiveToHeat(targetDog.sensitiveToHeat);
  }, [openedFromSettings, targetDog]);

  const MONTH_NAMES = t('dogProfile.monthNames').split(',');

  const formatDateToMMDDYYYY = (d: Date) => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatDateToDisplay = (d: Date) => {
    const dd = String(d.getDate());
    const month = MONTH_NAMES[d.getMonth()];
    const yyyy = d.getFullYear();
    return `${dd} ${month} ${yyyy}`;
  };

  const pickImage = async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const { status: existingStatus } = await ImagePicker.getMediaLibraryPermissionsAsync();

    if (existingStatus === 'denied') {
      Alert.alert(
        t('endWalkInfo.photoLibraryAccessNeeded'),
        t('dogProfile.photoLibraryMessage'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('record.openSettings'), onPress: openAppSettings },
        ],
      );
      return;
    }

    if (existingStatus !== 'granted') {
      await new Promise<void>((resolve) => {
        Alert.alert(
          t('dogProfile.allowPhotoAccess'),
          t('dogProfile.allowPhotoAccessMessage'),
          [{ text: t('common.continue'), onPress: () => resolve() }],
          { cancelable: false },
        );
      });

      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          t('endWalkInfo.photoLibraryAccessNeeded'),
          t('dogProfile.photoLibraryNotGrantedMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: openAppSettings },
          ],
        );
        return;
      }
    }

    setIsLoadingImage(true);
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.7,
    });

    setIsLoadingImage(false);

    if (!result.canceled && result.assets[0]) {
      setPhoto(result.assets[0].uri);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }
  };

  const takePhoto = async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const { status: existingStatus } = await ImagePicker.getCameraPermissionsAsync();

    if (existingStatus === 'denied') {
      Alert.alert(
        t('dogProfile.cameraAccessNeeded'),
        t('dogProfile.cameraAccessMessage'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('record.openSettings'), onPress: openAppSettings },
        ],
      );
      return;
    }

    if (existingStatus !== 'granted') {
      await new Promise<void>((resolve) => {
        Alert.alert(
          t('dogProfile.allowCameraAccess'),
          t('dogProfile.allowCameraAccessMessage'),
          [{ text: t('common.continue'), onPress: () => resolve() }],
          { cancelable: false },
        );
      });

      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          t('dogProfile.cameraAccessNeeded'),
          t('dogProfile.cameraNotGrantedMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: openAppSettings },
          ],
        );
        return;
      }
    }

    setIsLoadingImage(true);
    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.7,
    });

    setIsLoadingImage(false);

    if (!result.canceled && result.assets[0]) {
      setPhoto(result.assets[0].uri);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }
  };

  const handleImagePicker = () => {
    Alert.alert(
      t('dogProfile.addPhoto'),
      t('dogProfile.chooseHowToAddPhoto'),
      [
        { text: t('dogProfile.takePhoto'), onPress: takePhoto },
        { text: t('dogProfile.chooseFromLibrary'), onPress: pickImage },
        { text: t('common.cancel'), style: 'cancel' },
      ]
    );
  };

  /**
   * Build the full profile payload including breed-derived size + user-adjusted traits.
   */
  const buildProfileData = (parsedAge: number, dobValue: string | null) => {
    const profileData: any = {
      // Preserve the primary owner's id when editing a co-owned dog; only
      // stamp the current user as owner on a brand-new profile.
      user_id: isNewDog ? user!.id : (targetDog?.user_id || user!.id),
      name: name.trim(),
      nickname: nickname.trim() || undefined,
      breed: breed.trim() || undefined,
      size: SIZE_OPTIONS.find(o => o.key === dogSize)?.api || breedData?.apiSize || undefined,
      age: parsedAge,
      dob: dobValue || undefined,
      image: photo || undefined,
      // User-adjusted traits (1–10 on screen, 0–100 sent to backend)
      dogTolerance: dogTolerance * 10,
      nervousAroundPeople,
      offleashReliability: offleashReliability * 10,
      stimulationTolerance: stimulationTolerance * 10,
      highPreyDrive,
      walkingNeed: walkingNeed * 10,
      sensitiveToHeat,
    };

    return profileData;
  };

  const handleSave = async () => {
    if (!name.trim()) {
      Alert.alert(t('dogProfile.missingInfo'), t('dogProfile.pleaseEnterDogName'));
      return;
    }

    if (!breed) {
      Alert.alert(t('dogProfile.missingInfo'), t('dogProfile.pleaseSelectBreed'));
      return;
    }

    if (!breedData) {
      Alert.alert(t('common.error'), t('dogProfile.breedNotFound'));
      return;
    }

    let parsedAge: number | null = null;
    let dobValue: string | null = null;

    const dobInput = (age || '').trim();
    const mmddyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
    const yyyymmdd = /^\d{4}-\d{2}-\d{2}$/;
    const ddMonthyyyy = /^\d{1,2}\s+[A-Za-z]+\s+\d{4}$/;

    if (mmddyyyy.test(dobInput)) {
      const parts = dobInput.split('/').map((p) => parseInt(p, 10));
      const dt = new Date(parts[2], (parts[0] || 1) - 1, parts[1] || 1);
      if (isNaN(dt.getTime())) {
        Alert.alert(t('dogProfile.invalidDate'), t('dogProfile.invalidDateMMDDYYYY'));
        return;
      }
      const today = new Date();
      let years = today.getFullYear() - dt.getFullYear();
      const m = today.getMonth() - dt.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dt.getDate())) years--;
      parsedAge = years;
      dobValue = `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
    } else if (yyyymmdd.test(dobInput)) {
      const parts = dobInput.split('-').map((p) => parseInt(p, 10));
      const dt = new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1);
      if (isNaN(dt.getTime())) {
        Alert.alert(t('dogProfile.invalidDate'), t('dogProfile.invalidDateYYYYMMDD'));
        return;
      }
      const today = new Date();
      let years = today.getFullYear() - dt.getFullYear();
      const m = today.getMonth() - dt.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dt.getDate())) years--;
      parsedAge = years;
      dobValue = `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
    } else if (ddMonthyyyy.test(dobInput)) {
      const parts = dobInput.split(/\s+/);
      const day = parseInt(parts[0], 10);
      const monthToken = parts[1];
      const year = parseInt(parts[2], 10);
      const monthIndex = MONTH_NAMES.findIndex(m => m.toLowerCase().startsWith(monthToken.toLowerCase().slice(0, 3)));
      if (monthIndex === -1) {
        Alert.alert(t('dogProfile.invalidDate'), t('dogProfile.invalidDateDayMonthYear'));
        return;
      }
      const dt = new Date(year, monthIndex, day);
      if (isNaN(dt.getTime())) {
        Alert.alert(t('dogProfile.invalidDate'), t('dogProfile.invalidDateDayMonthYear'));
        return;
      }
      const today = new Date();
      let years = today.getFullYear() - dt.getFullYear();
      const m = today.getMonth() - dt.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dt.getDate())) years--;
      parsedAge = years;
      dobValue = formatDateToMMDDYYYY(dt);
    } else {
      const num = parseInt(dobInput, 10);
      if (!dobInput || isNaN(num) || num < 0 || num > 30) {
        Alert.alert(t('dogProfile.invalidAgeDate'), t('dogProfile.invalidAgeDateMessage'));
        return;
      }
      parsedAge = num;
    }

    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    const profileData = buildProfileData(parsedAge!, dobValue);

    // === New dog (explicit or onboarding) ===
    if (isNewDog || (!openedFromTab && !openedFromSettings)) {
      setIsSaving(true);
      try {
        await createDogProfile(profileData);
        logEvent('dog_profile_created', { source: openedFromTab ? 'profile' : 'onboarding' });
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        router.replace(openedFromTab ? '/(tabs)/profile' : '/(tabs)/explore');
      } catch (error: any) {
        console.error('Error creating dog profile:', error);
        Alert.alert(t('common.error'), error.message || t('dogProfile.failedToCreate'));
        setIsSaving(false);
      }
      return;
    }

    // === Editing existing dog from settings ===
    if (openedFromSettings && targetDog?.id && session) {
      setIsSaving(true);
      try {
        const updated: any = {
          name: name.trim(),
          nickname: nickname.trim() || undefined,
          breed: breed.trim() || undefined,
          size: SIZE_OPTIONS.find(o => o.key === dogSize)?.api || breedData.apiSize,
        };
        if (parsedAge !== null) updated.age = parsedAge;
        if (dobValue) updated.dob = dobValue;

        // User-adjusted traits (1–10 on screen, 0–100 sent to backend)
        updated.dogTolerance = dogTolerance * 10;
        updated.nervousAroundPeople = nervousAroundPeople;
        updated.offleashReliability = offleashReliability * 10;
        updated.stimulationTolerance = stimulationTolerance * 10;
        updated.highPreyDrive = highPreyDrive;
        updated.walkingNeed = walkingNeed * 10;
        updated.sensitiveToHeat = sensitiveToHeat;

        // Handle image upload if local URI
        if (typeof photo === 'string' && photo && !photo.startsWith('http')) {
          const convertedUri = await ensureJpegImage(photo);
          const uri = convertedUri;
          const uriParts = uri.split('/');
          const rawName = uriParts[uriParts.length - 1] || `dog_${Date.now()}.jpg`;
          const fileName = rawName.split(/[?#]/)[0];
          const extMatch = fileName.match(/\.([a-zA-Z0-9]+)$/);
          const ext = extMatch ? extMatch[1].toLowerCase() : 'jpg';
          const mimeType = ext === 'png' ? 'image/png' : 'image/jpeg';

          const form = new FormData();
          // @ts-ignore
          form.append('file', { uri, name: fileName, type: mimeType });
          form.append('filename', fileName);
          form.append('dog_id', targetDog.id);
          form.append('trail_id', '');
          form.append('user_id', '');

          const uploadResp = await fetch(`${API_URL}/uploads`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.accessToken}`,
              'accept': 'application/json',
            },
            body: form as any,
          });

          if (!uploadResp.ok) {
            const errText = await uploadResp.text();
            throw new Error(`Upload failed: ${uploadResp.status} ${errText}`);
          }

          const uploadData = await uploadResp.json();
          const publicUrl = uploadData.public_url || uploadData.publicUrl || uploadData.url;
          if (!publicUrl) throw new Error('Upload did not return a public URL');
          updated.image = publicUrl;
        } else if (photo) {
          updated.image = photo;
        }

        const resp = await fetch(`${API_URL}/dog/${targetDog.id}/dog-profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.accessToken}`,
          },
          body: JSON.stringify(updated),
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail?.[0]?.msg || 'Failed to update dog profile');
        }

        // Also update traits via dedicated endpoint
        try {
          await fetch(`${API_URL}/dog/${targetDog.id}/dog-traits`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.accessToken}`,
            },
            body: JSON.stringify({
              dogTolerance: dogTolerance * 10,
              nervousAroundPeople,
              offleashReliability: offleashReliability * 10,
              stimulationTolerance: stimulationTolerance * 10,
              highPreyDrive,
              walkingNeed: walkingNeed * 10,
              sensitiveToHeat,
            }),
          });
        } catch (e) {
          console.warn('Failed to update dog traits via dedicated endpoint:', e);
        }

        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        logEvent('dog_profile_updated', { source: 'settings' });
        try { await refreshDogProfile(); } catch (e) { /* ignore */ }
        router.replace('/(tabs)/profile');
      } catch (err: any) {
        console.error('Failed to update dog profile from settings endpoint:', err);
        Alert.alert(t('common.error'), err.message || t('dogProfile.failedToSave'));
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // === Editing existing dog from profile tab ===
    if (openedFromTab && targetDog) {
      setIsSaving(true);
      try {
        const updated: any = {
          ...targetDog,
          name: name.trim(),
          nickname: nickname.trim() || undefined,
          breed: breed.trim() || undefined,
          size: SIZE_OPTIONS.find(o => o.key === dogSize)?.api || targetDog.size,
        };
        if (parsedAge !== null) updated.age = parsedAge;
        if (dobValue) updated.dob = dobValue;
        if (photo) updated.image = photo;

        // User-adjusted traits (1–10 on screen, 0–100 sent to backend)
        updated.dogTolerance = dogTolerance * 10;
        updated.nervousAroundPeople = nervousAroundPeople;
        updated.offleashReliability = offleashReliability * 10;
        updated.stimulationTolerance = stimulationTolerance * 10;
        updated.highPreyDrive = highPreyDrive;
        updated.walkingNeed = walkingNeed * 10;
        updated.sensitiveToHeat = sensitiveToHeat;

        await updateDogProfile(updated as any);
        logEvent('dog_profile_updated', { source: 'profile' });
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        router.replace('/(tabs)/profile');
      } catch (err) {
        console.error('Failed to update dog profile from profile tab:', err);
        Alert.alert(t('common.error'), t('dogProfile.failedToSave'));
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // === New dog onboarding — create with user-adjusted traits ===
    setIsSaving(true);
    try {
      await createDogProfile(profileData);
      logEvent('dog_profile_created');
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      router.replace('/(tabs)/explore');
    } catch (error: any) {
      console.error('Error creating dog profile:', error);
      Alert.alert(t('common.error'), error.message || t('dogProfile.failedToCreate'));
      setIsSaving(false);
    }
  };

  const renderBreedItem = useCallback(({ item }: { item: string }) => (
    <TouchableOpacity
      style={[breedStyles.option, breed === item && breedStyles.optionSelected]}
      onPress={() => {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        breedChangedByUser.current = true;
        setBreed(item);
        setShowBreedPicker(false);
        setBreedSearch('');
      }}
    >
      <Text style={[breedStyles.optionText, breed === item && breedStyles.optionTextSelected]}>
        {item}
      </Text>
    </TouchableOpacity>
  ), [breed]);

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScrollView style={styles.container} contentContainerStyle={[styles.content, { paddingTop: insets.top + 20 }]}>
        {(targetDog || isNewDog) && (
          <TouchableOpacity style={styles.backButton} onPress={() => {
            if (openedFromTab) router.back(); else router.push('/profile');
          }}>
            <ArrowLeft size={20} color={theme.accentPrimary} />
          </TouchableOpacity>
        )}

        <View style={styles.header}>
          <Text style={styles.title}>{targetDog && !isNewDog ? t('dogProfile.editTitle') : t('dogProfile.newTitle')}</Text>
          <Text style={styles.subtitle}>{targetDog && !isNewDog ? t('dogProfile.editSubtitle') : t('dogProfile.newSubtitle')}</Text>
        </View>

        <TouchableOpacity
          style={styles.photoContainer}
          onPress={handleImagePicker}
          disabled={isLoadingImage}
        >
          {isLoadingImage ? (
            <ActivityIndicator size="large" color="#FFFE77" />
          ) : photo ? (
            <Image source={{ uri: photo }} style={styles.photo} />
          ) : (
            <View style={styles.photoPlaceholder}>
              <Camera size={32} color="#FFFE77" />
              <Text style={styles.photoPlaceholderText}>{t('dogProfile.addPhoto')}</Text>
            </View>
          )}
        </TouchableOpacity>

        <View style={styles.formSection}>
          <Text style={styles.label}>{t('dogProfile.name')} *</Text>
          <TextInput
            style={styles.input}
            value={name}
            onChangeText={setName}
            placeholder={t('dogProfile.namePlaceholder')}
            placeholderTextColor="#5a6040"
            autoCapitalize="words"
            testID="dog-name-input"
          />
        </View>

        <View style={styles.formSection}>
          <Text style={styles.label}>{t('dogProfile.nicknameOptional')}</Text>
          <TextInput
            style={styles.input}
            value={nickname}
            onChangeText={setNickname}
            placeholder={t('dogProfile.nicknamePlaceholder')}
            placeholderTextColor="#5a6040"
            autoCapitalize="words"
            testID="dog-nickname-input"
          />
        </View>

        {/* Breed Picker */}
        <View style={styles.formSection}>
          <Text style={styles.label}>{t('dogProfile.breed')} *</Text>
          <TouchableOpacity
            style={styles.pickerButton}
            onPress={() => {
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              setShowBreedPicker(true);
            }}
            testID="breed-picker-button"
          >
            <Text style={[styles.pickerButtonText, !breed && styles.pickerButtonTextPlaceholder]}>
              {breed || t('dogProfile.selectBreed')}
            </Text>
            <ChevronDown size={20} color="#a8ad8e" />
          </TouchableOpacity>
        </View>

        {/* Breed-derived size + editable traits */}
        {breedData && (
          <View style={breedStyles.traitsCard}>
            <Text style={breedStyles.traitsTitle}>Breed profile for {breed}</Text>
            <Text style={breedStyles.traitsSub}>Auto-filled from breed data — tap to adjust</Text>

            {/* Size (editable) */}
            <TouchableOpacity
              style={breedStyles.traitCard}
              onPress={() => {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                setShowSizePicker(true);
              }}
              activeOpacity={0.6}
            >
              <View style={breedStyles.traitCardHeader}>
                <Text style={breedStyles.traitCardLabel}>{t('dogProfile.size')}</Text>
                <View style={breedStyles.traitCardRight}>
                  <View style={[breedStyles.toggleBadge, breedStyles.toggleBadgeActive]}>
                    <Text style={[breedStyles.toggleBadgeText, breedStyles.toggleBadgeTextActive]}>
                      {SIZE_OPTIONS.find(o => o.key === dogSize)?.api || 'M'}
                    </Text>
                  </View>
                  <ChevronRight size={18} color={theme.textMuted} />
                </View>
              </View>
            </TouchableOpacity>

            {/* Slider trait cards */}
            {SLIDER_TRAITS.map((trait) => {
              const value = getSliderValue(trait.key);
              return (
                <TouchableOpacity
                  key={trait.key}
                  style={breedStyles.traitCard}
                  onPress={() => openSliderModal(trait.key)}
                  activeOpacity={0.6}
                >
                  <View style={breedStyles.traitCardHeader}>
                    <Text style={breedStyles.traitCardLabel}>{t(trait.labelKey)}</Text>
                    <View style={breedStyles.traitCardRight}>
                      <Text style={breedStyles.traitCardValue}>{value}</Text>
                      <ChevronRight size={18} color={theme.textMuted} />
                    </View>
                  </View>
                  <View style={breedStyles.progressBarTrack}>
                    <View style={[breedStyles.progressBarFill, { width: `${value * 10}%` }]} />
                  </View>
                  <View style={breedStyles.traitCardHintRow}>
                    <Text style={breedStyles.traitCardHint}>{t(trait.leftKey)}</Text>
                    <Text style={breedStyles.traitCardHint}>{t(trait.rightKey)}</Text>
                  </View>
                </TouchableOpacity>
              );
            })}

            {/* Toggle trait cards */}
            {TOGGLE_TRAITS.map((trait) => {
              const value = getToggleValue(trait.key);
              return (
                <TouchableOpacity
                  key={trait.key}
                  style={breedStyles.traitCard}
                  onPress={() => openToggleModal(trait.key)}
                  activeOpacity={0.6}
                >
                  <View style={breedStyles.traitCardHeader}>
                    <Text style={breedStyles.traitCardLabel}>{t(trait.labelKey)}</Text>
                    <View style={breedStyles.traitCardRight}>
                      <View style={[breedStyles.toggleBadge, value && breedStyles.toggleBadgeActive]}>
                        <Text style={[breedStyles.toggleBadgeText, value && breedStyles.toggleBadgeTextActive]}>
                          {value ? t('dogProfile.yes') : t('dogProfile.no')}
                        </Text>
                      </View>
                      <ChevronRight size={18} color={theme.textMuted} />
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        <View style={styles.formSection}>
          <Text style={styles.label}>{t('dogProfile.dateOfBirth')} *</Text>
          <TouchableOpacity
            style={[styles.input, styles.dateInput]}
            onPress={() => {
              setTempDobDate(dobDate || new Date());
              setShowDOBPicker(true);
            }}
            testID="dog-dob-input"
          >
            <Text style={age ? { color: theme.accentPrimary } : { color: '#5a6040' }}>
              {age || '10 August 2021'}
            </Text>
          </TouchableOpacity>
        </View>

        {Platform.OS === 'ios' && showDOBPicker ? (
          <Modal
            transparent
            animationType="slide"
            visible={showDOBPicker}
            onRequestClose={() => setShowDOBPicker(false)}
          >
            <TouchableWithoutFeedback onPress={() => setShowDOBPicker(false)}>
              <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)' }} />
            </TouchableWithoutFeedback>
            <View style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
              <View style={{ backgroundColor: '#12130f', padding: 12, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                <DateTimePicker
                  value={tempDobDate || dobDate || new Date()}
                  mode="date"
                  display="spinner"
                  textColor={theme.accentPrimary}
                  maximumDate={new Date()}
                  onChange={(_event, selectedDate) => {
                    if (selectedDate) {
                      setTempDobDate(selectedDate);
                    }
                  }}
                />
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 8 }}>
                  <TouchableOpacity
                    style={{ paddingHorizontal: 12, paddingVertical: 20 }}
                    onPress={() => {
                      setTempDobDate(null);
                      setShowDOBPicker(false);
                    }}
                  >
                    <Text style={{ color: '#9aa08a' }}>{t('common.cancel')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ paddingHorizontal: 12, paddingVertical: 20 }}
                    onPress={() => {
                      if (tempDobDate) {
                        setDobDate(tempDobDate);
                        setAge(formatDateToDisplay(tempDobDate));
                      }
                      setTempDobDate(null);
                      setShowDOBPicker(false);
                    }}
                  >
                    <Text style={{ color: theme.accentPrimary }}>{t('common.done')}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>
        ) : Platform.OS !== 'web' && showDOBPicker ? (
          <DateTimePicker
            value={dobDate || new Date()}
            mode="date"
            display={Platform.OS === 'ios' ? 'compact' : 'default'}
            textColor={Platform.OS === 'ios' ? theme.accentPrimary : undefined}
            maximumDate={new Date()}
            onChange={(_event, selectedDate) => {
              setShowDOBPicker(false);
              if (selectedDate) {
                setDobDate(selectedDate);
                setAge(formatDateToDisplay(selectedDate));
              }
            }}
          />
        ) : null}

        {openedFromSettings && targetDog?.id ? (
          <DogOwnersSection dogId={targetDog.id} />
        ) : null}

        <TouchableOpacity
          style={[styles.nextButton, { marginBottom: insets.bottom + 20 }]}
          onPress={handleSave}
          disabled={isSaving}
          testID={openedFromSettings ? 'save-button' : 'next-button'}
        >
          {isSaving ? (
            <ActivityIndicator color="#1a1f0a" />
          ) : (
            <Text style={styles.nextButtonText}>{targetDog && !isNewDog ? t('common.save') : (isNewDog ? t('profile.addDogProfile') : t('common.next'))}</Text>
          )}
        </TouchableOpacity>
      </ScrollView>

      {/* Breed Picker Modal */}
      <Modal
        visible={showBreedPicker}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => { setShowBreedPicker(false); setBreedSearch(''); }}
      >
        <View style={breedStyles.modalContainer}>
          <View style={breedStyles.modalHeader}>
            <Text style={breedStyles.modalTitle}>{t('dogProfile.selectBreed')}</Text>
            <TouchableOpacity
              onPress={() => { setShowBreedPicker(false); setBreedSearch(''); }}
              style={breedStyles.closeButton}
            >
              <X size={24} color={theme.accentPrimary} />
            </TouchableOpacity>
          </View>

          <View style={breedStyles.searchContainer}>
            <Search size={18} color="#5a6040" />
            <TextInput
              style={breedStyles.searchInput}
              value={breedSearch}
              onChangeText={setBreedSearch}
              placeholder={t('dogProfile.searchBreeds')}
              placeholderTextColor="#5a6040"
              autoCapitalize="words"
              autoFocus
            />
            {breedSearch.length > 0 && (
              <TouchableOpacity onPress={() => setBreedSearch('')}>
                <X size={16} color="#5a6040" />
              </TouchableOpacity>
            )}
          </View>

          <FlatList
            data={filteredBreeds}
            keyExtractor={(item) => item}
            renderItem={renderBreedItem}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={breedStyles.listContent}
            ListEmptyComponent={
              <View style={breedStyles.emptyContainer}>
                <Text style={breedStyles.emptyText}>{t('dogProfile.noBreedsFound')}</Text>
              </View>
            }
            initialNumToRender={30}
            maxToRenderPerBatch={30}
            windowSize={5}
          />
        </View>
      </Modal>

      {/* Trait Editor Modal */}
      <Modal
        visible={activeTrait !== null}
        animationType="slide"
        transparent={true}
        onRequestClose={closeModal}
      >
        <View style={breedStyles.modalOverlay}>
          <TouchableWithoutFeedback onPress={closeModal}>
            <View style={breedStyles.modalBackdrop} />
          </TouchableWithoutFeedback>
          <View style={breedStyles.modalSheet}>
            {/* Header */}
            <View style={breedStyles.sheetHeader}>
              <Text style={breedStyles.sheetTitle}>
                {activeSliderTrait ? t(activeSliderTrait.labelKey) : activeToggleTrait ? t(activeToggleTrait.labelKey) : ''}
              </Text>
              <TouchableOpacity onPress={closeModal} style={breedStyles.sheetCloseButton}>
                <X size={24} color={theme.accentPrimary} />
              </TouchableOpacity>
            </View>

            {/* Description */}
            <Text style={breedStyles.sheetDescription}>
              {activeSliderTrait ? t(activeSliderTrait.descKey) : activeToggleTrait ? t(activeToggleTrait.descKey) : ''}
            </Text>

            {/* Slider content */}
            {isSliderTrait && activeSliderTrait && (
              <>
                <Text style={breedStyles.sheetValue}>{Math.round(tempValue)}</Text>

                <Slider
                  minimumValue={1}
                  maximumValue={10}
                  step={1}
                  value={tempValue}
                  onValueChange={(v: number) => setTempValue(Math.round(v))}
                  minimumTrackTintColor="#FFFE77"
                  maximumTrackTintColor="#343A20"
                  thumbTintColor="#FFFFFF"
                  style={breedStyles.sheetSlider}
                />

                {/* Notch markers */}
                <View style={breedStyles.notchContainer}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((val) => (
                    <View key={val} style={breedStyles.notchItem}>
                      <View style={[
                        breedStyles.notchTick,
                        Math.round(tempValue) === val && breedStyles.notchTickActive,
                      ]} />
                    </View>
                  ))}
                </View>

                <View style={breedStyles.sliderLabelsRow}>
                  <Text style={breedStyles.sliderLabelLeft}>{t(activeSliderTrait.leftKey)}</Text>
                  <Text style={breedStyles.sliderLabelRight}>{t(activeSliderTrait.rightKey)}</Text>
                </View>
              </>
            )}

            {/* Toggle content */}
            {!isSliderTrait && activeToggleTrait && (
              <View style={breedStyles.toggleRow}>
                <TouchableOpacity
                  style={[breedStyles.toggleOption, tempToggle && breedStyles.toggleOptionActive]}
                  onPress={() => setTempToggle(true)}
                  activeOpacity={0.7}
                >
                  <Text style={[breedStyles.toggleOptionText, tempToggle && breedStyles.toggleOptionTextActive]}>
                    Yes
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[breedStyles.toggleOption, !tempToggle && breedStyles.toggleOptionActive]}
                  onPress={() => setTempToggle(false)}
                  activeOpacity={0.7}
                >
                  <Text style={[breedStyles.toggleOptionText, !tempToggle && breedStyles.toggleOptionTextActive]}>
                    No
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Done button */}
            <TouchableOpacity style={breedStyles.sheetDoneButton} onPress={confirmModal}>
              <Text style={breedStyles.sheetDoneButtonText}>{t('common.done')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Size Picker Modal */}
      <Modal
        visible={showSizePicker}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowSizePicker(false)}
      >
        <View style={breedStyles.modalOverlay}>
          <TouchableWithoutFeedback onPress={() => setShowSizePicker(false)}>
            <View style={breedStyles.modalBackdrop} />
          </TouchableWithoutFeedback>
          <View style={breedStyles.modalSheet}>
            <View style={breedStyles.sheetHeader}>
              <Text style={breedStyles.sheetTitle}>{t('dogProfile.size')}</Text>
              <TouchableOpacity onPress={() => setShowSizePicker(false)} style={breedStyles.sheetCloseButton}>
                <X size={24} color={theme.accentPrimary} />
              </TouchableOpacity>
            </View>
            <Text style={breedStyles.sheetDescription}>{t('dogProfile.sizeDescription')}</Text>
            <View style={breedStyles.sizeOptions}>
              {SIZE_OPTIONS.map((opt) => (
                <TouchableOpacity
                  key={opt.key}
                  style={[breedStyles.sizeOption, dogSize === opt.key && breedStyles.sizeOptionActive]}
                  onPress={() => {
                    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
                    setDogSize(opt.key);
                    setShowSizePicker(false);
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={[breedStyles.sizeOptionApi, dogSize === opt.key && breedStyles.sizeOptionApiActive]}>{opt.api}</Text>
                  <Text style={[breedStyles.sizeOptionLabel, dogSize === opt.key && breedStyles.sizeOptionLabelActive]}>{t(opt.labelKey)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const breedStyles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    backgroundColor: theme.backgroundPrimary,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.accentPrimary,
  },
  closeButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 20,
    marginVertical: 12,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'android' ? 4 : 12,
    borderRadius: 12,
    backgroundColor: theme.backgroundPrimary,
    borderWidth: 1,
    borderColor: '#3d4520',
    gap: 10,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: theme.accentPrimary,
    paddingVertical: Platform.OS === 'android' ? 8 : 0,
  },
  listContent: {
    paddingBottom: 40,
  },
  option: {
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#1a1f0a',
  },
  optionSelected: {
    backgroundColor: 'rgba(255, 254, 119, 0.12)',
  },
  optionText: {
    fontSize: 16,
    color: theme.accentPrimary,
  },
  optionTextSelected: {
    fontWeight: '700',
    color: '#FFFE77',
  },
  emptyContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  emptyText: {
    color: '#5a6040',
    fontSize: 16,
  },

  // ── Traits card ────────────────────────────────────────────────────────────

  traitsCard: {
    marginHorizontal: 0,
    marginBottom: 24,
    padding: 18,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: theme.borderSubtle,
    backgroundColor: theme.backgroundPrimary,
  },
  traitsTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.accentPrimary,
    marginBottom: 4,
  },
  traitsSub: {
    fontSize: 12,
    color: '#5a6040',
    marginBottom: 14,
  },

  // Size picker options
  sizeOptions: {
    flexDirection: 'row',
    gap: 10,
    marginVertical: 16,
  },
  sizeOption: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 4,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: theme.borderSubtle,
    backgroundColor: theme.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sizeOptionActive: {
    backgroundColor: 'rgba(255, 254, 119, 0.15)',
    borderColor: theme.accentPrimary,
  },
  sizeOptionApi: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.textMuted,
    marginBottom: 2,
  },
  sizeOptionApiActive: {
    color: theme.accentPrimary,
  },
  sizeOptionLabel: {
    fontSize: 11,
    color: theme.textMuted,
  },
  sizeOptionLabelActive: {
    color: theme.accentPrimary,
  },

  // Tappable trait card
  traitCard: {
    marginBottom: 8,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: theme.borderSubtle,
    backgroundColor: theme.backgroundPrimary,
  },
  traitCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  traitCardLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.accentPrimary,
    flex: 1,
  },
  traitCardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  traitCardValue: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.textMuted,
    minWidth: 28,
    textAlign: 'right',
  },

  // Progress bar
  progressBarTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.borderSubtle,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 2,
    backgroundColor: theme.accentPrimary,
  },

  // Hint labels
  traitCardHintRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 6,
  },
  traitCardHint: {
    fontSize: 12,
    color: theme.textMuted,
  },

  // Toggle badge
  toggleBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: theme.backgroundSecondary,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
  },
  toggleBadgeActive: {
    backgroundColor: 'rgba(255, 254, 119, 0.15)',
    borderColor: theme.accentPrimary,
  },
  toggleBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.textMuted,
  },
  toggleBadgeTextActive: {
    color: theme.accentPrimary,
  },

  // ── Trait editor modal (bottom sheet) ──────────────────────────────────────

  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalSheet: {
    backgroundColor: theme.backgroundPrimary,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 40,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: theme.borderSubtle,
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sheetTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.accentPrimary,
    flex: 1,
  },
  sheetCloseButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sheetDescription: {
    fontSize: 16,
    color: theme.textMuted,
    marginBottom: 24,
    lineHeight: 22,
  },
  sheetValue: {
    fontSize: 32,
    fontWeight: '700',
    color: theme.accentPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },
  sheetSlider: {
    height: 48,
    marginVertical: 4,
  },

  // Notch markers
  notchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: 8,
    paddingHorizontal: 2,
  },
  notchItem: {
    alignItems: 'center',
    flex: 1,
  },
  notchTick: {
    width: 1.5,
    height: 8,
    borderRadius: 1,
    backgroundColor: theme.borderSubtle,
  },
  notchTickActive: {
    height: 14,
    width: 2.5,
    backgroundColor: theme.accentPrimary,
  },


  // Slider end-labels
  sliderLabelsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  sliderLabelLeft: {
    fontSize: 12,
    color: theme.textMuted,
  },
  sliderLabelRight: {
    fontSize: 12,
    color: theme.textMuted,
  },

  // Toggle options (Yes / No)
  toggleRow: {
    flexDirection: 'row',
    gap: 12,
    marginVertical: 16,
  },
  toggleOption: {
    flex: 1,
    paddingVertical: 18,
    paddingHorizontal: 24,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: theme.borderSubtle,
    backgroundColor: theme.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleOptionActive: {
    backgroundColor: 'rgba(255, 254, 119, 0.15)',
    borderColor: theme.accentPrimary,
  },
  toggleOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.textMuted,
  },
  toggleOptionTextActive: {
    color: theme.accentPrimary,
    fontWeight: '700',
  },

  // Done button
  sheetDoneButton: {
    backgroundColor: theme.accentPrimary,
    borderRadius: 14,
    paddingVertical: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  sheetDoneButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1f0a',
  },
});